<template>
    <section v-if="blok" v-editable="blok" class="generalText" :style="styles">
        <div class="container mx-auto">
            <h1 v-if="blok.Title">{{ blok.Title }}</h1>
            <div class="rich-text" :class="`text-${blok.TextAlign}`">
                <rich-text v-if="blok.Text" :content="blok.Text" />
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import type { VNode } from 'vue';
import { createTextVNode, h } from 'vue';
import {
    BlockTypes,
    richTextResolver,
    type StoryblokRichTextNode,
    type StoryblokRichTextOptions,
} from '@storyblok/richtext';

const props: any = defineProps({ blok: Object });

const styles = computed(() => {
    return `
            ${props.blok.bgColor?.color ? `background-color: ${props.blok.bgColor.color};` : ''}
            ${props.blok.lineHeight && props.blok.lineHeight > 0 ? `--line-height: ${props.blok.lineHeight}px;` : `--line-height: 2rem;`}
            ${typeof props.blok.paddingTop === 'string' ? `padding-top: ${props.blok.paddingTop}rem;` : ''}
            ${typeof props.blok.paddingBottom === 'string' ? `padding-bottom: ${props.blok.paddingBottom}rem;` : ''}
            ${typeof props.blok.paddingLeft === 'string' ? `padding-left: ${props.blok.paddingLeft}rem;` : ''}
            ${typeof props.blok.paddingRight === 'string' ? `padding-right: ${props.blok.paddingRight}rem;` : ''}
            ${props.blok.paragraphFontSize ? `--paragraph-font-size: ${props.blok.paragraphFontSize}px;` : ''}
            ${props.blok.paragraphFontWeight ? `--paragraph-font-weight: ${props.blok.paragraphFontWeight};` : ''}`;
});

const options: StoryblokRichTextOptions<VNode> = {
    renderFn: h,
    textFn: createTextVNode,
    resolvers: {
        [BlockTypes.COMPONENT]: (node: StoryblokRichTextNode<VNode>) => {
            return node?.attrs?.body.map((blok) => {
                switch (blok.component) {
                    case 'OTCookieButton': {
                        return h(
                            'p',
                            {
                                innerHTML: `<button id="ot-sdk-btn" class="ot-sdk-show-settings">Cookie Settings</button>`,
                            },
                            node.children
                        );
                    }
                    case 'OTCookieTables': {
                        return h('div', { id: 'ot-sdk-cookie-policy' }, node.children);
                    }
                    case 'CheckmarkList': {
                        const cssStyles = `--checkmark-size: ${blok?.checkmarkSize}px;--checkmark-list-font-size: ${blok?.fontSize}px;`;
                        let listItems = '';
                        blok?.listItems?.forEach((item) => {
                            listItems += `<li>
                            <svg class="checkbox-circle-line" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2S2 6.477 2 12s4.477 10 10 10m5.457-12.543L11 15.914l-4.207-4.207l1.414-1.414L11 13.086l5.043-5.043z"/></svg>
                            ${item.text}
                        </li>`;
                        });
                        return h(
                            'ul',
                            { class: 'richtext-checkmark-list', style: cssStyles, innerHTML: listItems },
                            node.children
                        );
                    }
                    default: {
                        return 'Resolver not defined: ' + blok.component;
                    }
                }
            });
        },
    },
};

const richText = (content: any) => {
    return richTextResolver<VNode>(options).render(content.content);
};

// Hides OneTrust modal overlay
function hideOTModalOverlay() {
    const elementToHide = document.querySelector('#onetrust-consent-sdk .onetrust-pc-dark-filter');
    if (elementToHide) {
        // add delay for smoother animation
        setTimeout(() => {
            // @ts-expect-error - elementToHide is not in type
            elementToHide.style.display = 'none';
        }, 500);
    }
}

// HIDE OneTrust modal overlay, which sometimes stay after click on close button
function otModalClickHandler(event: MouseEvent) {
    // check if click was on OneTrust modal close btn
    // @ts-expect-error - event.target is not in type
    if (event.target?.id === 'close-pc-btn-handler') {
        const banner = document.getElementById('onetrust-banner-sdk');
        const bannerStyle = banner ? getComputedStyle(banner) : null;
        if (bannerStyle?.display === 'none') {
            // Hide modal overlay
            hideOTModalOverlay();
        }
    }
}

// removes OneTrust modal overlay after Esc button was pressed
function otModalEscBtnHandler(event: KeyboardEvent) {
    if (event.key === 'Escape' || event.key === 'Esc') {
        // Check for the Escape key

        const closeBtn = document.getElementById('close-pc-btn-handler');
        if (closeBtn) {
            // Check if the element is in the viewport
            const rect = closeBtn.getBoundingClientRect();
            const inViewport =
                rect.top < (window.innerHeight || document.documentElement.clientHeight) &&
                rect.left < (window.innerWidth || document.documentElement.clientWidth) &&
                rect.bottom > 0 &&
                rect.right > 0;

            if (inViewport) {
                // Hide modal overlay
                hideOTModalOverlay();
            }
        }
    }
}

onMounted(() => {
    // add event Listener on document, because we can't be sure that onetrust modals were already loaded onMounted
    document.addEventListener('click', otModalClickHandler);
    document.addEventListener('keydown', otModalEscBtnHandler);
});
onUnmounted(() => {
    document.removeEventListener('click', otModalClickHandler);
    document.removeEventListener('keydown', otModalEscBtnHandler);
});
</script>

<style scoped lang="postcss">
.generalText {
    @apply px-4 py-8 xl:px-0;
    &:deep(.css-float-left) {
        @apply float-left pr-2.5;
    }
    &:deep(.css-float-right) {
        @apply float-right pl-2.5;
    }
    &:deep(.css-text-sm) {
        @apply text-sm;
    }
    &:deep(.css-text-lg) {
        @apply text-lg;
    }
    &:deep(.css-text-xl) {
        @apply text-xl;
    }
    &:deep(.css-text-2xl) {
        @apply text-2xl;
    }
    &:deep(.css-text-3xl) {
        @apply text-3xl;
    }
    &:deep(.css-text-4xl) {
        @apply text-4xl;
    }
    &:deep(ul) {
        li {
            span:first-child.css-list-checkmarks:after {
                @apply content-[url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA1MTIgNTEyIj48IS0tIUZvbnQgQXdlc29tZSBQcm8gNi42LjAgYnkgQGZvbnRhd2Vzb21lIC0gaHR0cHM6Ly9mb250YXdlc29tZS5jb20gTGljZW5zZSAtIGh0dHBzOi8vZm9udGF3ZXNvbWUuY29tL2xpY2Vuc2UgKENvbW1lcmNpYWwgTGljZW5zZSkgQ29weXJpZ2h0IDIwMjQgRm9udGljb25zLCBJbmMuLS0+PHBhdGggZmlsbD0iI2Y2YWIzZCIgZD0iTTAgMjU2YTI1NiAyNTYgMCAxIDAgNTEyIDBBMjU2IDI1NiAwIDEgMCAwIDI1NnptMTM2IDBjMC02LjEgMi4zLTEyLjMgNy0xN2M5LjQtOS40IDI0LjYtOS40IDMzLjkgMGw0NyA0N2MzNy0zNyA3NC03NCAxMTEtMTExYzQuNy00LjcgMTAuOC03IDE3LTdzMTIuMyAyLjMgMTcgN2MyLjMgMi4zIDQuMSA1IDUuMyA3LjljLjYgMS41IDEgMi45IDEuMyA0LjRjLjIgMS4xIC4zIDIuMiAuMyAyLjJjLjEgMS4yIC4xIDEuMiAuMSAyLjVjLS4xIDEuNS0uMSAxLjktLjEgMi4zYy0uMSAuNy0uMiAxLjUtLjMgMi4yYy0uMyAxLjUtLjcgMy0xLjMgNC40Yy0xLjIgMi45LTIuOSA1LjYtNS4zIDcuOWMtNDIuNyA0Mi43LTg1LjMgODUuMy0xMjggMTI4Yy00LjcgNC43LTEwLjggNy0xNyA3cy0xMi4zLTIuMy0xNy03Yy0yMS4zLTIxLjMtNDIuNy00Mi43LTY0LTY0Yy00LjctNC43LTctMTAuOC03LTE3eiIvPjxwYXRoIGZpbGw9IndoaXRlIiBkPSJNMzY5IDE3NWM5LjQgOS40IDkuNCAyNC42IDAgMzMuOUwyNDEgMzM3Yy05LjQgOS40LTI0LjYgOS40LTMzLjkgMGwtNjQtNjRjLTkuNC05LjQtOS40LTI0LjYgMC0zMy45czI0LjYtOS40IDMzLjkgMGw0NyA0N0wzMzUgMTc1YzkuNC05LjQgMjQuNi05LjQgMzMuOSAweiIvPjwvc3ZnPg==")];
                @apply w-4 h-4 block absolute text-[#f6ab3d] -left-5 top-0 bg-white leading-[normal] rounded-[50%];
            }

            span:first-child.css-list-checkmarks {
                @apply relative pl-2;
            }
        }
    }

    :deep(.richtext-checkmark-list) {
        font-size: var(--checkmark-list-font-size);
        @apply ml-0 list-none;
        svg.checkbox-circle-line {
            @apply text-secondary relative -top-[2px];
            font-size: var(--checkmark-size);
            margin-right: calc(var(--checkmark-size) / 2);
        }
        li {
            @apply mb-2.5 flex flex-nowrap items-start;
        }
    }
}
</style>
